import type { CSSProperties, ReactNode } from "react";
import { ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * The composer pill — ONE writing field for every surface that has one: the
 * Message Board / issue comment thread (`ChatComposer`), Franklin's open chat
 * (`SidebarChatInput`) and the collapsed corner pill (`CollapsedChatBar`).
 *
 * Slots, not variants. `leading` is whatever precedes the field — the author's
 * avatar on the board, Franklin's mark on the collapsed pill, nothing in the
 * open chat; `actions` is the trailing icon run, which differs per surface
 * (attach + mention on the board, add + web search in the chat). Everything
 * that makes the field *look* like the field lives here, so restyling it once
 * restyles all three.
 */
export function ComposerShell({
  leading,
  actions,
  className,
  style,
  children,
}: {
  leading?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Geometry a surface pins in pixels — see `CORNER_COMPOSER_HEIGHT`. */
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div
      style={style}
      className={cn(
        "flex items-center gap-2 rounded-[999px] px-1.5 py-1",
        "border border-[#e2e2e4] bg-white",
        className
      )}
    >
      {leading}
      <div className="min-w-0 flex-1">{children}</div>
      {actions && <div className="flex flex-shrink-0 items-center gap-0.5">{actions}</div>}
    </div>
  );
}

/** Ghost icon button in the pill's trailing run (attach, mention, web search). */
export const COMPOSER_ACTION_CLASS = "h-7 w-7 text-muted-foreground hover:text-foreground";

/**
 * Left inset for Franklin's corner composer — used by BOTH the collapsed pill
 * (where it positions the Franklin mark, and the "Ask …" text after it) and the
 * open panel (where it positions the text directly), so the two states share one
 * value and the content starts at the same distance from the border in each
 * (round-8 feedback).
 *
 * The board composer keeps the shell's default `px-1.5`: its 32px avatar already
 * fills the pill's rounded cap, whereas the chat's 18px mark and its bare text
 * read as jammed against the border without the extra room.
 */
export const CORNER_COMPOSER_INSET_CLASS = "pl-4";

/**
 * Height of Franklin's corner composer, in pixels — the second half of the
 * inset token above, and used the same way: the collapsed pill pins its shell
 * to it and the open panel's field floors its shell at it, so the two states
 * are exactly as tall as each other and nothing grows or shrinks as the panel
 * opens or closes (round-9 feedback).
 */
export const CORNER_COMPOSER_HEIGHT = 40;

/**
 * Height of the field inside that shell: the shell minus its `py-1` (4px top
 * and bottom) and its 1px border. Pinning the field — not just the shell —
 * keeps the two states' text on the same baseline, and makes the open panel's
 * auto-growing textarea start at exactly one shell height.
 */
export const CORNER_COMPOSER_FIELD_HEIGHT = CORNER_COMPOSER_HEIGHT - 10;

/**
 * Classes for the round send cap. Exported because the collapsed chat pill
 * renders it as a plain `span` — the whole pill is already the click target
 * there — and must still match the button pixel for pixel.
 */
export const COMPOSER_SEND_CLASS = {
  base: "flex h-7 w-7 items-center justify-center rounded-full disabled:opacity-100",
  /** Something to send (and the collapsed pill's always-live "start" cap). */
  active: "bg-primary text-primary-foreground hover:bg-primary/90",
  muted: "bg-muted text-muted-foreground hover:bg-muted",
} as const;

/** The pill's send button. `children` overrides the icon (the chat's stop square). */
export function ComposerSendButton({
  canSend,
  onClick,
  label,
  describedBy,
  children,
}: {
  canSend: boolean;
  onClick: () => void;
  label: string;
  /** Id of the text saying why the button is disabled, when there is a reason worth reading. */
  describedBy?: string;
  children?: ReactNode;
}) {
  return (
    <Button
      type="button"
      size="icon"
      onClick={onClick}
      disabled={!canSend}
      aria-label={label}
      aria-describedby={describedBy}
      className={cn(
        COMPOSER_SEND_CLASS.base,
        canSend ? COMPOSER_SEND_CLASS.active : COMPOSER_SEND_CLASS.muted
      )}
    >
      {children ?? <ArrowUp className="h-4 w-4" />}
    </Button>
  );
}
